// Les PARCOURS ANNEXES (R-PAR-01 à R-PAR-05) : des séries de sessions hors saison, qu'on lance
// quand on en a envie. Rien ici ne pousse, ne relance ni ne compte de retard : c'est tout
// l'intérêt d'avoir sorti l'électronique du chemin critique (ADR 0009).
import { Link } from 'react-router';
import { useMagasin, magasin } from '../donnees/magasin';
import { avancementDeParcours, sessionSuivanteDeParcours } from '@partage/programme';
import type { BundleParcours } from '@partage/contenu';
import { Html } from '../composants/Html';

/** Un parcours déjà téléchargé : on connaît ses sessions, donc son avancement. */
function Actif({ bundle }: { bundle: BundleParcours }) {
  const { etat } = useMagasin();
  const { faites, total } = avancementDeParcours(etat, bundle);
  const suivante = sessionSuivanteDeParcours(etat, bundle);

  return (
    <div className="carte">
      <div className="ligne">
        <h3 style={{ margin: 0 }}>{bundle.titre}</h3>
        <span className="badge accent">{faites} / {total}</span>
      </div>
      <p style={{ whiteSpace: 'pre-wrap' }}>{bundle.objectif}</p>
      <progress value={faites} max={total} style={{ width: '100%' }} />
      {suivante ? (
        <p>
          <Link className="bouton principal" to={`/sessions/${suivante.id}`}>
            {faites === 0 ? 'Commencer' : 'Continuer'} — {suivante.id} · {suivante.titre}
          </Link>
        </p>
      ) : (
        <p className="doux">Parcours terminé. Le livrable est dans ton portfolio.</p>
      )}
      {bundle.defis.length ? (
        <details>
          <summary className="petit-texte">{bundle.defis.length} défis, jamais obligatoires</summary>
          {bundle.defis.map((d) => (
            <div key={d.id} style={{ marginTop: '0.6em' }}>
              <strong>{d.titre}</strong>
              <Html html={d.enonce_html} />
              {d.criteres.length ? <ul className="petit-texte">{d.criteres.map((c, i) => <li key={i}>{c}</li>)}</ul> : null}
            </div>
          ))}
        </details>
      ) : null}
      <details>
        <summary className="petit-texte">Les {total} séances</summary>
        <ol className="petit-texte">
          {bundle.sessions.map((s) => {
            const statut = etat.progression.sessions[s.id]?.statut;
            return (
              <li key={s.id}>
                <Link to={`/sessions/${s.id}`}>{s.titre}</Link>
                {statut === 'terminee' ? ' ✓' : statut === 'en_cours' ? ' — en cours' : ''}
              </li>
            );
          })}
        </ol>
      </details>
    </div>
  );
}

/** Un parcours que le catalogue annonce mais qu'on n'a pas encore lancé. */
function Propose({ id, titre, sessions }: { id: string; titre: string; sessions: number }) {
  async function lancer() {
    // Le livrable du parcours n'est connu qu'une fois le bundle téléchargé ; l'écran se
    // recharge juste après, et le livrable rejoint le portfolio à ce moment-là (R-PAR-05).
    await magasin.emettre('parcours_active', { parcours: id });
    await magasin.chargerContenu();
  }
  return (
    <div className="carte">
      <div className="ligne">
        <h3 style={{ margin: 0 }}>{titre}</h3>
        <span className="badge">{sessions} séances</span>
      </div>
      <p className="doux">
        Hors programme : ce parcours ne compte pas dans les 33 séances d’une saison et ne décale rien.
        Tu le commences quand tu veux, tu l’arrêtes quand tu veux.
      </p>
      <button className="principal" onClick={() => void lancer()}>Ouvrir ce parcours</button>
    </div>
  );
}

export function Parcours() {
  const { etat, parcours, catalogue } = useMagasin();
  const annonces = catalogue?.parcours ?? [];
  const actifs = annonces.filter((p) => etat.parcours[p.id]);
  const proposes = annonces.filter((p) => !etat.parcours[p.id]);

  return (
    <>
      <h2>Parcours</h2>
      <p className="doux">
        Des séries de séances à côté du programme, pour le plaisir ou pour combler un manque.
        Elles comptent dans ta régularité et génèrent leurs cartes, mais elles ne bloquent jamais
        une saison.
      </p>

      {annonces.length === 0 ? <p className="vide">Aucun parcours disponible pour l’instant.</p> : null}

      {actifs.map((p) => {
        const bundle = parcours[p.id];
        return bundle
          ? <Actif bundle={bundle} key={p.id} />
          : <div className="carte" key={p.id}><h3 style={{ margin: 0 }}>{p.titre}</h3><p className="doux">Téléchargement du contenu à la prochaine connexion.</p></div>;
      })}

      {proposes.map((p) => <Propose id={p.id} titre={p.titre} sessions={p.sessions} key={p.id} />)}
    </>
  );
}
